import { useSelector } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import greenRoutes from "./GeneralRoutes";
import PosDeviceGate from "./components/pos-device-gate/PosDeviceGate";
import SubscriptionRouteGate from "./components/universe-company-directrory/SubscriptionRouteGate.jsx";
import useSubscriptionAccess from "./authorization/hooks/useSubscriptionAccess.js";
import { activeSessionClaims } from "./http_common.js";
import { canOpenCompanyAccount, canOpenClientProfile, canOpenPOS, userHomePath } from "./authorization/companyUserAccess.js";

export default function Authorization() {
  const { isAuthenticated: storedStaffAuth, activeStaff, sessionToken: staffToken } = useSelector(
    (state) => state.universeStaffs,
  );

  const staffClaims = activeSessionClaims(staffToken, ["STAFF_ACCESS"]);
  const universeStaffAuth = storedStaffAuth && !!staffClaims;
  const universeStaffRole = staffClaims?.role;
  const systemAdminAuth = useSelector(
    (state) => state.systemAdminFunction.isAuthenticated,
  );
  const financeAuth = useSelector(
    (state) => state.financeFunction.isAuthenticated,
  );
  const executiveAuth = useSelector(
    (state) => state.executiveFunction.isAuthenticated,
  );

  const agentAuth = useSelector((state) => state.agentFunction.isAuthenticated);
  const adminAuth = useSelector((state) => state.adminFunction.isAuthenticated);
  const staffAuth = useSelector(
    (state) => state.universeStaffs.isAuthenticated,
  );
  const applicantAuth = useSelector(
    (state) => state.applictaionForm.isAuthenticated,
  );
  const clientSession = useSelector(state => state.clientFunction);
  const clientClaims = activeSessionClaims(clientSession?.sessionToken, ["CLIENT_ACCESS", "USER_ACCESS"]);
  const clientAuth = clientSession?.isAuthenticated && !!clientClaims;
  const terminalSession = useSelector(state => state.terminalActions);
  const terminalAuth = clientAuth && terminalSession.isAuthenticated &&
    terminalSession.registerData?.clientId === clientClaims.clientId &&
    (!terminalSession.registerData?.representiveId || terminalSession.registerData.representiveId === clientClaims.userId);
  const data = clientSession?.companiesDB?.clientData;
  const user = clientSession?.companiesDB?.staffData;
  const accountHome = userHomePath(clientClaims?.accessType, user, data);
  const loginPath = "/access/client-portal/authentication/sign-in";
  const posAllowed = clientAuth && canOpenPOS(clientClaims?.accessType, user);
  const subscriptionAccess = useSubscriptionAccess();

  return (
    <Routes>
      {greenRoutes.map((req, index) => {
        if (req.isPublic) {
          return <Route path={req.path} element={req.element} key={index} />;
        } else if (req.isSystemAdmin) {
          return (
            <Route
              path={req.path}
              element={
                systemAdminAuth ? (
                  req.element
                ) : (
                  <Navigate to="/information-technology/system-administration/login" />
                )
              }
              key={index}
            />
          );
        } else if (req.isUniverseStaff) {
          const allowedStaffRoles = req.allowedStaffRoles || [];

          const hasRoleAccess =
            universeStaffAuth && allowedStaffRoles.includes(universeStaffRole);

          return (
            <Route
              path={req.path}
              element={
                hasRoleAccess ? (
                  req.element
                ) : (
                  <Navigate to="/administration/staff-login" replace />
                )
              }
              key={index}
            />
          );
        } else if (req.isexecutive) {
          return (
            <Route
              path={req.path}
              element={
                executiveAuth ? req.element : <Navigate to="/executive/login" />
              }
              key={index}
            />
          );
        } else if (req.isAdmin) {
          return (
            <Route
              path={req.path}
              element={
                adminAuth ? (
                  req.element
                ) : (
                  <Navigate to="/administration/admin_login" />
                )
              }
              key={index}
            />
          );
        } else if (req.isFinance) {
          return (
            <Route
              path={req.path}
              element={
                financeAuth ? (
                  req.element
                ) : (
                  <Navigate to="/finance/finance_login" />
                )
              }
              key={index}
            />
          );
        } else if (req.isAgent) {
          return (
            <Route
              path={req.path}
              element={
                agentAuth ? req.element : <Navigate to="/agent_unit/login" />
              }
              key={index}
            />
          );
        } else if (req.isStaff) {
          return (
            <Route
              path={req.path}
              element={staffAuth ? req.element : <Navigate to="/staff_login" />}
              key={index}
            />
          );
        } else if (req.isCompanyUser) {
          return <Route path={req.path} element={clientAuth && clientClaims.accessType === "USER_ACCESS" ? req.element : <Navigate to={clientAuth ? accountHome : loginPath} replace />} key={index} />;
        } else if (req.isCompanyAccount) {
          return <Route path={req.path} element={clientAuth && canOpenCompanyAccount(clientClaims.accessType, user) ? (req.name === "purchase-orders" ? <SubscriptionRouteGate area="purchase orders">{req.element}</SubscriptionRouteGate> : req.element) : <Navigate to={clientAuth ? accountHome : loginPath} replace />} key={index} />;
        } else if (req.isClientProfile) {
          return <Route path={req.path} element={clientAuth && canOpenClientProfile(clientClaims.accessType, user) ? req.element : <Navigate to={clientAuth ? accountHome : loginPath} replace />} key={index} />;
        } else if (req.isClient) {
          return (
            <Route
              path={req.path}
              element={
                clientAuth && canOpenCompanyAccount(clientClaims.accessType, user) ? (
                  req.element
                ) : (
                  <Navigate to={clientAuth ? accountHome : loginPath} replace />
                )
              }
              key={index}
            />
          );
        } else if (req.isPointOfSale) {
          return (
            <Route
              path={req.path}
              element={
                <PosDeviceGate>
                  {terminalAuth && posAllowed ? (
                    <SubscriptionRouteGate area="Point of Sale terminal">{req.element}</SubscriptionRouteGate>
                  ) : (
                    <Navigate to={posAllowed ? "/clients/workspace/terminal-login" : clientAuth ? accountHome : loginPath} replace />
                  )}
                </PosDeviceGate>
              }
              key={index}
            />
          );
        } else if (req.isClientGuest) {
          return (
            <Route
              path={req.path}
              element={
                clientAuth ? (
                  <Navigate to={accountHome} replace />
                ) : (
                  req.element
                )
              }
              key={index}
            />
          );
        } else if (req.isPointOfSaleGuest) {
          return (
            <Route
              path={req.path}
              element={
                <PosDeviceGate>
                  {terminalAuth && posAllowed && subscriptionAccess.active ? (
                    <Navigate
                      to="/clients/point-of-sale/workspace/inventory-transactions"
                      replace
                    />
                  ) : (
                    posAllowed ? <SubscriptionRouteGate area="Point of Sale login">{req.element}</SubscriptionRouteGate> : <Navigate to={clientAuth ? accountHome : loginPath} replace />
                  )}
                </PosDeviceGate>
              }
              key={index}
            />
          );
        } else if (req.isApplicants) {
          return (
            <Route
              path={req.path}
              element={
                applicantAuth ? (
                  req.element
                ) : (
                  <Navigate to="/admission_status" />
                )
              }
              key={index}
            />
          );
        } else {
          return (
            <Route path="*" element={<Navigate to="/" replace />} key={index} />
          );
        }
      })}
    </Routes>
  );
}
